import { useEffect, useMemo, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ArrowLeft, Check, Download, Loader2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { DocumentGeneratingState } from "@/components/agent/documents/DocumentGeneratingState";
import { DocumentPreview } from "@/components/agent/documents/DocumentPreview";
import { GenieDocumentEditor } from "@/components/genie-documents/GenieDocumentEditor";
import { GenieDocumentFormatGallery } from "@/components/genie-documents/GenieDocumentFormatGallery";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useGenerateGenieDocument } from "@/hooks/genie-documents/useGenerateGenieDocument";
import { useSaveGenieDocument } from "@/hooks/genie-documents/useSaveGenieDocument";
import { cn } from "@/lib/utils";
import { genieDocumentBrand } from "@/services/genie-documents/genieDocumentBrand";
import { downloadGenieDocumentPdf } from "@/services/genie-documents/genieDocumentPdf";
import type { Agent } from "@/types/agent/agent";
import {
  findGenieDocumentFormat,
  GeneratedGenieDocument,
  type GenieDocumentFormat,
} from "@/types/genie-documents/genie-document";
import type { GenieDocumentEntry } from "@/hooks/genie-documents/useGenieDocuments";
import type { TopicCluster } from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface GenieDocumentBuilderSheetProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  /** Knowledge sources only — branded documents are filtered out upstream. */
  sources: DocumentWithMetadata[];
  clusters?: TopicCluster[];
  /** Preselects a format when the sheet is opened from a format card. */
  initialFormatKey?: string | null;
  /** Preselects every source in a topic, when opened from the knowledge brain. */
  initialClusterId?: string | null;
  /** Set to reopen a saved document for editing instead of creating a new one. */
  editingEntry?: GenieDocumentEntry | null;
  onSaved?: () => void;
}

const PRIMARY_CTA =
  "bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 transition-all disabled:opacity-50";

export const GenieDocumentBuilderSheet = ({
  isOpen,
  onClose,
  agent,
  sources,
  clusters = [],
  initialFormatKey,
  initialClusterId,
  editingEntry,
  onSaved,
}: GenieDocumentBuilderSheetProps) => {
  const [formatKey, setFormatKey] = useState<string | null>(initialFormatKey ?? null);
  const [purpose, setPurpose] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [doc, setDoc] = useState<GeneratedGenieDocument | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const generate = useGenerateGenieDocument();
  const save = useSaveGenieDocument();

  const brand = useMemo(() => genieDocumentBrand(agent), [agent]);
  const format = findGenieDocumentFormat(formatKey);

  // Every open is a fresh session: either a blank build seeded by the card that
  // was clicked, or the saved document being reopened.
  useEffect(() => {
    if (!isOpen) return;

    if (editingEntry) {
      setFormatKey(editingEntry.stored.formatKey ?? null);
      setPurpose(editingEntry.stored.purpose ?? "");
      setSelectedIds(editingEntry.stored.sourceDocumentIds ?? []);
      setDoc({
        title: editingEntry.stored.title,
        subtitle: editingEntry.stored.subtitle,
        template: editingEntry.stored.template,
        sections: editingEntry.stored.sections,
      });
      setSavedId(editingEntry.row.id);
      setIsEditing(true);
      return;
    }

    const seeded = findGenieDocumentFormat(initialFormatKey);
    const cluster = initialClusterId
      ? clusters.find((entry) => entry.id === initialClusterId)
      : undefined;

    setFormatKey(seeded?.key ?? null);
    setPurpose(seeded?.purpose ?? "");
    setSelectedIds(cluster ? [...cluster.documentIds] : []);
    setDoc(null);
    setSavedId(null);
    setIsEditing(false);
    // Clusters are recomputed on every knowledge change; keying off the id keeps
    // this from re-seeding a build the owner is halfway through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingEntry, initialFormatKey, initialClusterId]);

  const toggleSource = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectCluster = (cluster: TopicCluster) => {
    const ids = new Set(cluster.documentIds);
    const allSelected = cluster.documentIds.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.has(id))
        : [...new Set([...prev, ...cluster.documentIds])],
    );
  };

  const handleGenerate = async () => {
    if (!format?.template) {
      toast.error("Choose a format first");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one source document");
      return;
    }

    try {
      const result = await generate.mutateAsync({
        agentId: agent.id,
        template: format.template,
        purpose: purpose.trim() || undefined,
        documentIds: selectedIds,
        genieName: agent.name,
      });

      setDoc({ ...result.document, template: result.document.template ?? format.template });
      setIsEditing(false);

      if (result.meta.truncated) {
        toast.message("Used a focused portion of your longer documents to keep it tight.");
      }
      if (result.meta.skippedDocuments.length > 0) {
        toast.message(
          `Skipped ${result.meta.skippedDocuments.length} source${
            result.meta.skippedDocuments.length === 1 ? "" : "s"
          } with no readable text yet.`,
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't create the document");
    }
  };

  const handleSave = async () => {
    if (!doc) return;

    if (!doc.title.trim()) {
      toast.error("Give the document a title before saving");
      return;
    }

    const result = await save.mutateAsync({
      id: savedId ?? undefined,
      agentId: agent.id,
      title: doc.title.trim(),
      subtitle: doc.subtitle?.trim() || undefined,
      template: doc.template,
      sections: doc.sections,
      formatKey: formatKey ?? undefined,
      purpose: purpose.trim() || undefined,
      sourceDocumentIds: selectedIds,
    });

    setSavedId(result.document.id);
    toast.success(
      result.created
        ? result.published
          ? "Saved — it's live on your Genie Page"
          : "Saved as a draft — publish it from the guides list when you're ready"
        : "Changes saved",
    );
    onSaved?.();
    onClose();
  };

  const handleDownload = async () => {
    if (!doc) return;
    setIsDownloading(true);
    try {
      await downloadGenieDocumentPdf(doc, brand);
    } catch {
      toast.error("Couldn't export the PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  const canGenerate = Boolean(format?.template) && selectedIds.length > 0;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        showClose={false}
        className="flex h-full w-full flex-col p-0 sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl"
      >
        <VisuallyHidden>
          <SheetTitle>{editingEntry ? "Edit branded document" : "Create a branded document"}</SheetTitle>
        </VisuallyHidden>

        <div className="flex items-start justify-between border-b p-6 sm:p-8">
          <div className="pr-4">
            <h2 className="text-xl font-bold text-gray-900">
              {editingEntry ? doc?.title || "Edit document" : "Create a branded document"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {editingEntry
                ? "Edits here update the document everywhere it is used, including your Genie Page."
                : "Built only from the sources you pick. Nothing is invented."}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          {generate.isPending ? (
            <DocumentGeneratingState brand={brand} template={format?.template ?? "faq"} />
          ) : !doc ? (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Choose a format
                </label>
                <GenieDocumentFormatGallery
                  variant="sheet"
                  selectedKey={formatKey}
                  onSelect={(selected: GenieDocumentFormat) => {
                    setFormatKey(selected.key);
                    // A format's seed focus is a starting point, never an
                    // override of something the owner already typed.
                    setPurpose((current) => current || selected.purpose || "");
                  }}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  What should it focus on? <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  value={purpose}
                  onChange={(event) => setPurpose(event.target.value)}
                  placeholder="e.g. Membership renewals, for new members"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-genie-primary"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  A short focus keeps the document tight and on-topic.
                </p>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Source documents</label>
                  <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
                </div>

                {clusters.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {clusters.map((cluster) => {
                      const allSelected =
                        cluster.documentIds.length > 0 &&
                        cluster.documentIds.every((id) => selectedIds.includes(id));
                      return (
                        <button
                          key={cluster.id}
                          type="button"
                          onClick={() => selectCluster(cluster)}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                            allSelected
                              ? "border-genie-primary bg-genie-primary/10 text-genie-primary"
                              : "border-gray-300 bg-white text-gray-600 hover:border-genie-primary/40",
                          )}
                        >
                          {allSelected && <Check className="h-3 w-3" />}
                          {cluster.label}
                          <span className="text-gray-400">{cluster.documentIds.length}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {sources.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500">
                    This Genie has no knowledge sources yet. Add some in the knowledge base first.
                  </div>
                ) : (
                  <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                    {sources.map((source) => {
                      const active = selectedIds.includes(source.id);
                      return (
                        <button
                          key={source.id}
                          type="button"
                          onClick={() => toggleSource(source.id)}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all",
                            active
                              ? "border-genie-primary bg-genie-primary/5"
                              : "border-gray-200 bg-white hover:border-gray-300",
                          )}
                        >
                          <Checkbox checked={active} className="pointer-events-none shrink-0" />
                          <span className="truncate text-sm text-gray-800">{source.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : isEditing ? (
            <GenieDocumentEditor doc={doc} onChange={setDoc} />
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <DocumentPreview doc={doc} brand={brand} />
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-wrap justify-start gap-3 border-t bg-white px-6 py-4 sm:px-8">
          {!doc ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={!canGenerate || generate.isPending} className={PRIMARY_CTA}>
                {generate.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…
                  </>
                ) : (
                  "Create document"
                )}
              </Button>
            </>
          ) : (
            <>
              {!editingEntry && (
                <Button variant="outline" onClick={() => setDoc(null)} className="gap-1.5">
                  <ArrowLeft className="h-4 w-4" /> Back to sources
                </Button>
              )}
              <Button variant="outline" onClick={() => setIsEditing((value) => !value)} className="gap-1.5">
                <Pencil className="h-4 w-4" />
                {isEditing ? "Preview" : "Edit content"}
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleDownload()}
                disabled={isDownloading}
                className="gap-1.5"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download PDF
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={save.isPending}
                className={`gap-1.5 ${PRIMARY_CTA}`}
              >
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savedId ? "Save changes" : "Save to this Genie"}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default GenieDocumentBuilderSheet;
